import { useMemo } from "react";
import type { MatchEvent } from "../../../store/useMatchLogStore";
import type { Match } from "../types";

/** Per-player aggregate stats derived from live + queued events. */
export interface PlayerStats {
  playerId: string;
  playerName: string;
  jerseyNumber: number;
  teamSide: "home" | "away";
  /** Total events where this player is the author */
  totalEvents: number;
  points: number;
  fieldGoalsMade: number;
  fieldGoalsAttempted: number;
  threePointersMade: number;
  threePointersAttempted: number;
  freeThrowsMade: number;
  freeThrowsAttempted: number;
  rebounds: number;
  assists: number;
  steals: number;
  blocks: number;
  turnovers: number;
  foulsCommitted: number;
  foulsReceived: number;
}

const EMPTY: PlayerStats[] = [];

/**
 * Aggregates per-player statistics from match events.
 *
 * Author player is always `event.player_id`.
 * Destination / target player varies by event type and lives in `event.data`:
 *   - Pass → `data.receiver_id`
 *   - FoulCommitted → `data.target_player_id`
 *   - Duel → `data.target_player_id`
 */
export function usePlayerStats(
  match: Match | null,
  events: MatchEvent[],
): PlayerStats[] {
  return useMemo(() => {
    if (!match || events.length === 0) return EMPTY;

    const homeTeamId = match.home_team.id;

    // Build a lookup: playerId → { name, jersey, teamSide }
    const playerLookup = new Map<
      string,
      { name: string; jersey: number; teamSide: "home" | "away" }
    >();
    for (const p of match.home_team.players) {
      playerLookup.set(p.id, {
        name: p.short_name || p.full_name,
        jersey: p.jersey_number,
        teamSide: "home",
      });
    }
    for (const p of match.away_team.players) {
      playerLookup.set(p.id, {
        name: p.short_name || p.full_name,
        jersey: p.jersey_number,
        teamSide: "away",
      });
    }

    const statsMap = new Map<string, PlayerStats>();

    const getOrCreate = (playerId: string): PlayerStats => {
      let s = statsMap.get(playerId);
      if (!s) {
        const info = playerLookup.get(playerId);
        s = {
          playerId,
          playerName: info?.name ?? `#${playerId}`,
          jerseyNumber: info?.jersey ?? 0,
          teamSide: info?.teamSide ?? "home",
          totalEvents: 0,
          points: 0,
          fieldGoalsMade: 0,
          fieldGoalsAttempted: 0,
          threePointersMade: 0,
          threePointersAttempted: 0,
          freeThrowsMade: 0,
          freeThrowsAttempted: 0,
          rebounds: 0,
          assists: 0,
          steals: 0,
          blocks: 0,
          turnovers: 0,
          foulsCommitted: 0,
          foulsReceived: 0,
        };
        statsMap.set(playerId, s);
      }
      return s;
    };

    for (const event of events) {
      const authorId = event.player_id;
      if (!authorId) continue;

      const isHomeTeam = event.team_id === homeTeamId;
      const author = getOrCreate(authorId);

      // Ensure teamSide is correct based on event data
      if (!playerLookup.has(authorId)) {
        author.teamSide = isHomeTeam ? "home" : "away";
      }

      author.totalEvents++;

      const data = event.data ?? {};
      const type = event.type;

      switch (type) {
        case "Shot": {
          const shotType = String(data.shot_type ?? "").toLowerCase();
          const outcome = String(data.outcome ?? "").toLowerCase();
          const isThree = shotType === "3pt";
          const isFT = shotType === "ft" || shotType === "free_throw";
          const made = outcome === "goal" || outcome === "made";

          if (isFT) {
            author.freeThrowsAttempted++;
            if (made) {
              author.freeThrowsMade++;
              author.points += 1;
            }
          } else {
            author.fieldGoalsAttempted++;
            if (isThree) author.threePointersAttempted++;
            if (made) {
              author.fieldGoalsMade++;
              author.points += isThree ? 3 : 2;
              if (isThree) author.threePointersMade++;
            }
          }
          break;
        }

        case "FreeThrow": {
          author.freeThrowsAttempted++;
          const outcome = String(data.outcome ?? "").toLowerCase();
          if (outcome === "made" || outcome === "goal") {
            author.freeThrowsMade++;
            author.points += 1;
          }
          break;
        }

        case "Rebound":
          author.rebounds++;
          break;

        case "Assist":
          author.assists++;
          break;

        case "Steal":
          author.steals++;
          break;

        case "Block":
          author.blocks++;
          break;

        case "Turnover":
          author.turnovers++;
          break;

        case "FoulCommitted": {
          author.foulsCommitted++;
          const fouledId = data.target_player_id ?? data.opponent_id;
          if (fouledId) {
            getOrCreate(fouledId).foulsReceived++;
          }
          break;
        }

        default:
          break;
      }
    }

    // Return sorted: home first, then away, within each group by jersey number
    return Array.from(statsMap.values()).sort((a, b) => {
      if (a.teamSide !== b.teamSide) {
        return a.teamSide === "home" ? -1 : 1;
      }
      return a.jerseyNumber - b.jerseyNumber;
    });
  }, [match, events]);
}
